import { ReactTFC } from "../../../../utils/fp/missing-ts-parts";
import React, { useEffect, useState } from "react";
import { useStore } from "../../../../store/hooks/use-store";
import { usePageContext } from "../../../../global/page-context";
import { useOpaqueValue } from "../../../../hooks/use-opaque-value";
import { useConfiguredAdyenInstance } from "../../checkout-page-layout/CheckoutComponent/adyen/use-adyen-instance";
import UIElement from "@adyen/adyen-web/dist/types/components/UIElement";
import { PayPalElementProps } from "@adyen/adyen-web/dist/types/components/PayPal/types";
import Core from "@adyen/adyen-web/dist/types/core";
import { PaymentMethodOptions } from "@adyen/adyen-web/dist/types/types";
import { ApplePayResponse } from "@swo/storefront";
import { css } from "@emotion/react";
import { SmallLoadingSpinner } from "../../../../routes/modal-loading-spinner";
import cn from "classnames";
import { CheckoutHeadline } from "../../checkout-page-layout/CheckoutComponent/shared/CheckoutHeadline";
import { SwarovskiButton } from "../../../../components/_shared-components/ui-elements/SwarovskiButton";
import { CSS_COLORS } from "../../../../styles/styles-css-variables";
import { FONTS, fontSize } from "../../../../styles/fonts";
import type { TranslatedValue } from "@swo/storefront";
import { T } from "../../../../components/T";

export const MakeApplePayment: ReactTFC<{
  data: ApplePayResponse["data"];
  onPaymentAuthorised: (token: string) => void;
  onPaymentCancelled: () => void;
  translations: {
    headline: string;
    paragraph: TranslatedValue;
    notAvailable: TranslatedValue;
    cancelApplePayPaymentButton: TranslatedValue;
  };
}> = props => {
  const { state } = useStore();
  const { clientKey, environment } = state.config.adyen;

  const adyenInstance = useConfiguredAdyenInstance(
    (state: any, component: UIElement<PayPalElementProps>) => {
      console.log("Apple Pay additional data", state, component);
    },
    (state, component) => {
      console.log("Apple Pay on change", state, component);
    },
    clientKey,
    environment,
  );

  if (!adyenInstance) {
    return null;
  }

  return (
    <MakeApplePaymentWithInstance
      adyenInstance={adyenInstance.underlying}
      data={props.data}
      clientKey={clientKey}
      headline={props.translations.headline}
      paragraph={props.translations.paragraph}
      translations={{
        cancelPaymentButton: props.translations.cancelApplePayPaymentButton,
      }}
      applePayNotAvailable={props.translations.notAvailable}
      userPaymentName={`#${props.data.orderNumber}`}
      onPaymentAuthorised={props.onPaymentAuthorised}
      onPaymentCancelled={props.onPaymentCancelled}
    />
  );
};

const MakeApplePaymentWithInstance: ReactTFC<{
  adyenInstance: Core;
  data: ApplePayResponse["data"];
  clientKey: string;
  headline: string;
  paragraph: TranslatedValue;
  applePayNotAvailable: TranslatedValue;
  onPaymentAuthorised: (token: string) => void;
  onPaymentCancelled: () => void;
  userPaymentName: string;
  translations: {
    cancelPaymentButton: TranslatedValue;
  };
}> = props => {
  const [isApplePayAvailableForThisDevice, setApplePayAvailableForThisDevice] = useState<boolean | null>(null);
  const applePayContainer = "apple-pay-container";

  useEffect(() => {
    const options: PaymentMethodOptions<"applepay"> = {
      clientKey: props.clientKey,
      configuration: {
        merchantId: props.data.merchantId,
        merchantName: props.data.merchantName,
      },
      amount: {
        currency: props.data.currencyCode,
        value: props.data.centAmount,
      },
      countryCode: props.data.countryCode,
      totalPriceLabel: props.userPaymentName,
      showPayButton: true,
      supportedNetworks: props.data.brands,
      onError: err => {
        console.log("Apple Pay error", err);
      },
      onAuthorized: (resolve, reject, event) => {
        console.log("Apple Pay onAuthorized", event);
        resolve();
      },
      onSubmit: state => {
        const applePayToken = state?.data?.paymentMethod?.applePayToken;

        if (typeof applePayToken === "string") {
          props.onPaymentAuthorised(applePayToken);
        } else {
          console.error(`Invalid ApplePay submit.`, state);
        }
      },
      buttonColor: "black",
    };
    const el = props.adyenInstance.create("applepay", options);

    el.isAvailable()
      .then(isAvailable => {
        if (isAvailable) {
          el.mount(`#${applePayContainer}`);

          setApplePayAvailableForThisDevice(true);
        } else {
          setApplePayAvailableForThisDevice(false);
        }
      })
      .catch(e => {
        console.log("Apple Pay not available", e);
        setApplePayAvailableForThisDevice(false);
      });
  }, []);

  const paragraphStyles = [
    FONTS.SwarovskiFutura300,
    fontSize.x3,
    css`
      display: block;
      text-align: center;
      margin-bottom: 12px;
    `,
  ];

  return (
    <div
      css={css`
        text-align: center;
      `}
    >
      {isApplePayAvailableForThisDevice === null && (
        <div
          css={css`
            display: flex;
            justify-content: center;
          `}
        >
          <SmallLoadingSpinner color={"green"} />
        </div>
      )}

      <div
        className={cn({ hidden: isApplePayAvailableForThisDevice === null })}
        css={css`
          &.hidden {
            display: none;
          }
        `}
      >
        <div
          css={css`
            margin-bottom: 40px;
          `}
        >
          <CheckoutHeadline
            css={css`
              margin-top: 0;
            `}
          >
            {props.headline}
          </CheckoutHeadline>

          <p css={paragraphStyles}>
            {isApplePayAvailableForThisDevice ? <T>{props.paragraph}</T> : <T>{props.applePayNotAvailable}</T>}
          </p>
        </div>

        <div
          id={applePayContainer}
          css={css`
            display: flex;
            justify-content: center;
          `}
        />

        <div
          css={css`
            margin-top: 30px;
          `}
        >
          <SwarovskiButton variant={"plain-underline"} as={"button"} size={"small"} onClick={props.onPaymentCancelled}>
            <T>{props.translations.cancelPaymentButton}</T>
          </SwarovskiButton>
        </div>
      </div>
    </div>
  );
};
